if (!customElements.get('product-option-drawer')) {
  class ProductOptionDrawer extends HTMLElement {
    constructor() {
      super();
      this.productOverview = null;
      this.drawer = null;
      this.button = null;
      this.drawerContent = null;
      this.originalOptions = null;
      this.isInitialized = false;
      this.syncLock = false; // Prevent feedback loops
      this.dragBarEnabled = false;
      this.isDragging = false;
      this.startY = 0;
      this.currentY = 0;
      this.initialDrawerHeight = 0;
      this.dragThreshold = 100; // pixels to drag before closing
      // Membership state
      this.membershipFooterState = null; // 'add-to-bag' | 'membership-required'
    }

    connectedCallback() {
      this.productOverview = this.closest('product-overview');
      if (!this.productOverview) return;

      // Wait for product-overview to initialize
      if (this.productOverview.product && this.productOverview.variant) {
        this.init();
      } else {
        this.productOverview.addEventListener('product-overview:initialized', () => {
          this.init();
        });
      }
    }

    init() {
      if (this.isInitialized) return;

      this.originalOptions = this.productOverview.querySelector('.shopify-block--option-selectors');
      if (!this.originalOptions) return;

      this.dragBarEnabled = this.dataset.dragBar === 'true';
      this.createButton();
      this.createDrawer();
      this.setupEventListeners();
      this.updateButtonDisplay();
      this.updateAddToBagButton();
      this.isInitialized = true;
    }

    createButton() {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'product-option-drawer__button';
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-label', 'Open product options');

      const buttonContent = document.createElement('div');
      buttonContent.className = 'product-option-drawer__button-content';

      const label = document.createElement('span');
      label.className = 'product-option-drawer__button-label';
      label.textContent = 'Size';

      const values = document.createElement('span');
      values.className = 'product-option-drawer__button-values';

      buttonContent.appendChild(values);
      this.button.appendChild(label);
      this.button.appendChild(buttonContent);

      this.appendChild(this.button);
    }

    createDrawer() {
      this.drawer = document.createElement('div');
      this.drawer.className = 'product-option-drawer__drawer';
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');

      const backdrop = document.createElement('div');
      backdrop.className = 'product-option-drawer__backdrop';

      const panel = document.createElement('div');
      panel.className = 'product-option-drawer__panel';

      const header = document.createElement('div');
      header.className = 'product-option-drawer__header';

      // Add drag bar if enabled
      if (this.dragBarEnabled && window.theme && window.theme.elements && window.theme.elements.svg_drag_bar) {
        const dragBar = document.createElement('div');
        dragBar.className = 'product-option-drawer__drag-bar';
        dragBar.innerHTML = window.theme.elements.svg_drag_bar;
        header.appendChild(dragBar);
      }

      const title = document.createElement('h3');
      title.className = 'product-option-drawer__title';
      title.textContent = 'Select Options';

      this.drawerContent = document.createElement('div');
      this.drawerContent.className = 'product-option-drawer__content';

      // Create footer container - content will be rendered dynamically based on membership
      this.footer = document.createElement('div');
      this.footer.className = 'product-option-drawer__footer';

      header.appendChild(title);
      panel.appendChild(header);
      panel.appendChild(this.drawerContent);
      panel.appendChild(this.footer);
      this.drawer.appendChild(backdrop);
      this.drawer.appendChild(panel);

      document.body.appendChild(this.drawer);
    }

    setupEventListeners() {
      // Button click to open drawer
      this.button.addEventListener('click', () => this.openDrawer());

      // Backdrop click to close
      const backdrop = this.drawer.querySelector('.product-option-drawer__backdrop');
      backdrop.addEventListener('click', () => this.closeDrawer());

      // Listen for variant changes to update button and sync drawer
      this.productOverview.addEventListener('product-overview:variant-change', () => {
        this.updateButtonDisplay();
        this.updateAddToBagButton();
        // Sync drawer selections if drawer is open and we're not in a sync lock
        if (this.isDrawerOpen() && !this.syncLock) {
          this.syncSelectionsToDrawer();
        }
      });

      // Listen for option availability changes to sync classes
      this.productOverview.addEventListener('product-overview:option-availability-change', () => {
        if (this.isDrawerOpen()) {
          this.syncAvailabilityClasses();
        }
      });

      // Listen for main option changes to sync to drawer
      this.originalOptions.addEventListener('change', (event) => {
        this.handleMainOptionChange(event);
      });

      // Listen for option changes in the drawer
      this.drawerContent.addEventListener('change', (event) => {
        this.handleDrawerOptionChange(event);
      });

      // Escape key to close
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isDrawerOpen()) {
          this.closeDrawer();
        }
      });

      // Setup drag functionality if enabled
      if (this.dragBarEnabled) {
        this.setupDragFunctionality();
      }
    }

    openDrawer() {
      // Clone the current option selectors into the drawer
      this.cloneOptionsToDrawer();

      // Remove any existing classes
      this.drawer.classList.remove('closing');
      this.drawer.classList.add('open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.button.setAttribute('aria-expanded', 'true');

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Focus the first option in the drawer after animation starts
      setTimeout(() => {
        const firstOption = this.drawerContent.querySelector('input[type="radio"]');
        if (firstOption) {
          firstOption.focus();
        }
      }, 100);
    }

    closeDrawer(skipAnimation = false) {
      // Add closing class for exit animation
      this.drawer.classList.remove('open');
      this.drawer.classList.add('closing');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.button.setAttribute('aria-expanded', 'false');

      // Clean up any drag-related state
      const panel = this.drawer.querySelector('.product-option-drawer__panel');
      if (panel) {
        panel.classList.remove('dragging', 'drag-threshold-approaching');
        // Only reset transform if we're not in the middle of a drag animation
        if (skipAnimation) {
          panel.style.transform = '';
          panel.style.transition = '';
        }
      }
      this.drawer.classList.remove('dragging-active');
      this.isDragging = false;

      // Get animation duration from CSS variable, fallback to 300ms
      const duration = parseInt(getComputedStyle(document.documentElement)
        .getPropertyValue('--drawer-animation-duration').replace('ms', '')) || 300;

      // Wait for animation to complete before hiding and restoring scroll
      setTimeout(() => {
        this.drawer.classList.remove('closing');

        // Clean up any remaining transforms and transitions
        if (panel) {
          panel.style.transform = '';
          panel.style.transition = '';
        }

        // Restore body scroll
        document.body.style.overflow = '';
      }, duration);

      // Return focus to button
      this.button.focus();
    }

    setupDragFunctionality() {
      const panel = this.drawer.querySelector('.product-option-drawer__panel');
      const dragBar = this.drawer.querySelector('.product-option-drawer__drag-bar');
      const header = this.drawer.querySelector('.product-option-drawer__header');

      if (!panel || !dragBar || !header) return;

      // Create drag handle area (30px from top of drawer)
      const dragHandle = document.createElement('div');
      dragHandle.className = 'product-option-drawer__drag-handle';
      header.insertBefore(dragHandle, header.firstChild);

      // Touch events
      dragHandle.addEventListener('touchstart', this.handleDragStart.bind(this), { passive: false });
      dragHandle.addEventListener('touchmove', this.handleDragMove.bind(this), { passive: false });
      dragHandle.addEventListener('touchend', this.handleDragEnd.bind(this), { passive: false });

      // Mouse events
      dragHandle.addEventListener('mousedown', this.handleDragStart.bind(this));
      document.addEventListener('mousemove', this.handleDragMove.bind(this));
      document.addEventListener('mouseup', this.handleDragEnd.bind(this));
    }

    handleDragStart(event) {
      if (!this.isDrawerOpen()) return;

      this.isDragging = true;
      const panel = this.drawer.querySelector('.product-option-drawer__panel');
      this.initialDrawerHeight = panel.offsetHeight;

      // Add dragging class to disable CSS animations and transitions during drag
      panel.classList.add('dragging');
      // Also disable the drawer's open animation
      this.drawer.classList.add('dragging-active');

      // Get initial Y position
      if (event.type === 'touchstart') {
        this.startY = event.touches[0].clientY;
      } else {
        this.startY = event.clientY;
      }

      this.currentY = this.startY;

      // Prevent text selection during drag
      event.preventDefault();
      document.body.style.userSelect = 'none';
    }

    handleDragMove(event) {
      if (!this.isDragging || !this.isDrawerOpen()) return;

      event.preventDefault();

      // Get current Y position
      if (event.type === 'touchmove') {
        this.currentY = event.touches[0].clientY;
      } else {
        this.currentY = event.clientY;
      }

      // Calculate drag distance (positive = dragging down, negative = dragging up)
      const dragDistance = this.currentY - this.startY;
      const panel = this.drawer.querySelector('.product-option-drawer__panel');

      // Only allow dragging down (positive values), clamp negative values to 0
      const transformY = Math.max(0, dragDistance);

      // Apply transform immediately for responsive dragging
      panel.style.transform = `translateY(${transformY}px)`;

      // Add visual feedback when approaching close threshold (only when dragging down)
      if (dragDistance > this.dragThreshold * 0.7) {
        panel.classList.add('drag-threshold-approaching');
      } else {
        panel.classList.remove('drag-threshold-approaching');
      }
    }

    handleDragEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      document.body.style.userSelect = '';

      const panel = this.drawer.querySelector('.product-option-drawer__panel');
      const dragDistance = this.currentY - this.startY;

      // Remove dragging and visual feedback classes from panel
      panel.classList.remove('dragging', 'drag-threshold-approaching');

      // Check if we've dragged down far enough to close (only consider downward drags)
      if (dragDistance > this.dragThreshold) {
        // Keep dragging-active class to prevent CSS animations during close
        // Animate drawer sliding down and off screen before closing
        panel.style.transition = 'transform 0.3s ease-in';
        panel.style.transform = 'translateY(100vh)'; // Slide completely off screen

        // Close the drawer after animation (skip default close animation since we're doing custom)
        setTimeout(() => {
          this.closeDrawer(true);
        }, 300);
      } else {
        // Remove dragging-active class for snap-back animation
        this.drawer.classList.remove('dragging-active');

        // Snap back to original position with smooth animation
        panel.style.transition = 'transform 0.3s ease-out';
        panel.style.transform = 'translateY(0)';

        // Clean up the transition after animation completes
        setTimeout(() => {
          panel.style.transition = '';
        }, 300);
      }
    }

    isDrawerOpen() {
      return this.drawer.classList.contains('open');
    }

    cloneOptionsToDrawer() {
      // Clear existing content
      this.drawerContent.innerHTML = '';

      // Clone the option selectors but exclude color swatches
      const optionsClone = this.originalOptions.cloneNode(true);

      // Remove color swatches from the clone
      const colorSwatches = optionsClone.querySelectorAll('.js-product-swatches');
      colorSwatches.forEach(swatch => swatch.remove());

      // Also remove any color-related option groups
      const colorOptions = optionsClone.querySelectorAll('[data-option*="olor"], [data-option*="OLOR"]');
      colorOptions.forEach(option => option.remove());

      // Make selectors unique by adding 'drawer-' prefix to input names and IDs
      this.makeSelectorsUnique(optionsClone);

      this.drawerContent.appendChild(optionsClone);

      // Sync current selections and availability classes
      this.syncSelectionsToDrawer();
      this.syncAvailabilityClasses();
    }

    makeSelectorsUnique(element) {
      // Find all radio inputs and make their names and IDs unique
      const radioInputs = element.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(input => {
        // Add prefix to name to avoid conflicts with main form
        if (input.name) {
          input.name = 'drawer-' + input.name;
        }

        // Add prefix to ID to avoid conflicts
        if (input.id) {
          const oldId = input.id;
          input.id = 'drawer-' + input.id;

          // Update corresponding label's 'for' attribute
          const label = element.querySelector(`label[for="${oldId}"]`);
          if (label) {
            label.setAttribute('for', input.id);
          }
        }
      });

      // Update any other elements that might reference the original IDs
      const labels = element.querySelectorAll('label');
      labels.forEach(label => {
        if (label.getAttribute('for') && !label.getAttribute('for').startsWith('drawer-')) {
          label.setAttribute('for', 'drawer-' + label.getAttribute('for'));
        }
      });
    }

    syncSelectionsToDrawer() {
      if (this.syncLock) return;

      // Get currently selected options from the original form
      const originalSelections = this.originalOptions.querySelectorAll('input[type="radio"]:checked');

      originalSelections.forEach(originalInput => {
        // Skip color options
        const isColorOption = originalInput.closest('.js-product-swatches');
        if (isColorOption) return;

        // Find matching input in drawer by converting main name to drawer name and matching value
        const drawerName = 'drawer-' + originalInput.name;
        const matchingInput = this.drawerContent.querySelector(
          `input[type="radio"][name="${drawerName}"][value="${originalInput.value}"]`
        );

        if (matchingInput && matchingInput.checked !== originalInput.checked) {
          matchingInput.checked = true;
        }
      });
    }

    syncAvailabilityClasses() {
      if (!this.drawerContent) return;

      // Get all radio inputs from original options (excluding color options)
      const originalInputs = this.originalOptions.querySelectorAll('input[type="radio"]:not(.js-product-swatches input)');

      originalInputs.forEach(originalInput => {
        // Skip color options
        const isColorOption = originalInput.closest('.js-product-swatches');
        if (isColorOption) return;

        // Find matching input in drawer
        const drawerName = 'drawer-' + originalInput.name;
        const matchingDrawerInput = this.drawerContent.querySelector(
          `input[type="radio"][name="${drawerName}"][value="${originalInput.value}"]`
        );

        if (matchingDrawerInput) {
          // Sync availability classes
          const availabilityClasses = ['impossible-combination', 'option-unavailable'];
          availabilityClasses.forEach(className => {
            if (originalInput.classList.contains(className)) {
              matchingDrawerInput.classList.add(className);
            } else {
              matchingDrawerInput.classList.remove(className);
            }
          });

          // Sync tabindex attribute for impossible combinations
          if (originalInput.getAttribute('tabindex') === '-1') {
            matchingDrawerInput.setAttribute('tabindex', '-1');
          } else {
            matchingDrawerInput.removeAttribute('tabindex');
          }
        }
      });
    }

    handleMainOptionChange() {
      // Sync main option changes to drawer if drawer is open and we're not in a sync lock
      if (this.isDrawerOpen() && !this.syncLock) {
        this.syncLock = true;
        this.syncSelectionsToDrawer();
        this.syncLock = false;
      }
    }

    handleDrawerOptionChange(event) {
      const changedInput = event.target;
      if (changedInput.type !== 'radio') return;

      // Check if this is an impossible combination - prevent selection
      if (changedInput.classList.contains('impossible-combination')) {
        event.preventDefault();
        changedInput.checked = false;
        return;
      }

      // Prevent sync feedback loops
      this.syncLock = true;

      try {
        // Convert drawer input name back to original name
        const originalName = changedInput.name.replace('drawer-', '');

        // Find the corresponding input in the original form
        const originalInput = this.originalOptions.querySelector(
          `input[type="radio"][name="${originalName}"][value="${changedInput.value}"]`
        );

        if (originalInput && originalInput.checked !== changedInput.checked) {
          // Update the original form
          originalInput.checked = true;

          // Trigger change event on the original input to update the variant
          originalInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Update button display
        setTimeout(() => {
          this.updateButtonDisplay();
        }, 50);

      } finally {
        // Always release the sync lock
        setTimeout(() => {
          this.syncLock = false;
        }, 100);
      }
    }

    updateButtonDisplay() {
      if (!this.button || !this.productOverview) return;

      const valuesElement = this.button.querySelector('.product-option-drawer__button-values');
      if (!valuesElement) return;

      // Get all currently selected options except color
      const selectedOptions = [];
      const nonColorGroups = this.originalOptions.querySelectorAll('.js-product-radios:not(.js-product-swatches)');

      nonColorGroups.forEach(group => {
        const selectedInput = group.querySelector('input[type="radio"]:checked');
        if (selectedInput) {
          selectedOptions.push(selectedInput.value);
        }
      });

      if (selectedOptions.length > 0) {
        // Clear existing content
        valuesElement.innerHTML = '';

        // Create individual value elements with chevrons
        selectedOptions.forEach((optionValue) => {
          const valueSpan = document.createElement('span');
          valueSpan.className = 'product-option-drawer__button-value';
          valueSpan.textContent = optionValue;

          const chevron = document.createElement('span');
          chevron.className = 'product-option-drawer__button-value-chevron';
          chevron.innerHTML = theme.elements?.svg_arrow_down || '▼';

          valuesElement.appendChild(valueSpan);
          valuesElement.appendChild(chevron);
        });

        this.button.classList.add('has-selections');
      } else {
        valuesElement.innerHTML = '';
        this.button.classList.remove('has-selections');
      }
    }

    handleAddToBag() {
      // Find the original add to cart button and trigger its form submission
      const originalAtcButton = this.productOverview.querySelector('button[data-add-to-cart]');
      if (originalAtcButton) {
        // Close drawer first
        this.closeDrawer();

        // Trigger the original form submission
        const form = originalAtcButton.closest('form');
        if (form) {
          // Create and dispatch a submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
    }

    /**
     * Get customer membership data from window.theme.customer.membership
     * @returns {Object|null} Customer membership data or null if not available
     */
    getCustomerMembership() {
      try {
        return window.theme?.customer?.membership || null;
      } catch {
        return null;
      }
    }

    /**
     * Get product membership requirements from productOverview
     * @returns {Object|null} Product membership data or null if not available
     */
    getProductMembership() {
      try {
        return this.productOverview?.product?.membership || null;
      } catch {
        return null;
      }
    }

    /**
     * Check if customer meets the product's membership requirements
     * @returns {Object} { hasAccess: boolean, reason: string }
     */
    validateMembershipAccess() {
      const productMembership = this.getProductMembership();
      const customerMembership = this.getCustomerMembership();
      const isDeprecated = Boolean(productMembership?.deprecated);

      // If no membership required, customer has access
      if (!productMembership?.membership_required) {
        return { hasAccess: true, reason: 'no-membership-required' };
      }

      // If membership is required but customer has no active membership
      if (!customerMembership?.active) {
        return { hasAccess: false, reason: 'no-membership' };
      }

      // Get hierarchy levels
      const productHierarchyLevel = productMembership.membership_level_required?.hierarchy_level || 0;
      const customerHierarchyLevel = customerMembership.level?.hierarchy_level || 0;

      // Check group compatibility if both have groups
      const productGroup = productMembership.membership_group_required;
      const customerGroup = customerMembership.group;

      if (productGroup?.handle && customerGroup?.handle) {
        if (productGroup.handle !== customerGroup.handle) {
          // Different membership groups - customer doesn't have access
          return { hasAccess: false, reason: 'group-mismatch' };
        }
      }

      // Check hierarchy level
      if (customerHierarchyLevel >= productHierarchyLevel) {
        return { hasAccess: true, reason: isDeprecated ? 'membership-valid-deprecated' : 'membership-valid' };
      }

      // Customer has a membership but insufficient level
      return { hasAccess: false, reason: 'insufficient-level' };
    }

    /**
     * Get the membership CTA text based on product requirements
     * @returns {string} The CTA text for membership-restricted products
     */
    getMembershipCTAText() {
      const productMembership = this.getProductMembership();
      const customerMembership = this.getCustomerMembership();
      const membershipValidation = this.validateMembershipAccess();
      const isDeprecated = Boolean(productMembership?.deprecated);

      if (!productMembership?.membership_required) {
        return '';
      }

      if (isDeprecated && (!customerMembership?.active || membershipValidation.reason === 'insufficient-level')) {
        return 'Available With Any Greyson Membership';
      }

      const levelTitle = productMembership.membership_level_required?.title || 'member';
      const groupTitle = productMembership.membership_group_required?.title;

      // Match the Liquid template logic
      if (levelTitle === 'member' || !levelTitle) {
        if (groupTitle) {
          // Remove " Membership" suffix if present for cleaner display
          const cleanTitle = groupTitle.replace(' Membership', '');
          return `Available With Any ${cleanTitle} Membership`;
        }
        return 'Available With Any Greyson Membership';
      }

      return `Available With A ${levelTitle} Membership`;
    }

    /**
     * Get the membership landing page URL
     * @returns {string} The membership landing page URL
     */
    getMembershipLandingPageURL() {
      const productMembership = this.getProductMembership();
      const baseUrl = window.theme?.settings?.membership_landing_page_url || '/pages/membership-landing';
      const levelTitle = productMembership?.membership_level_required?.title || 'member';

      // If it's a specific membership level (not just "member"), add hash anchor
      if (levelTitle && levelTitle !== 'member') {
        const handle = `${levelTitle} membership`.toLowerCase().replace(/\s+/g, '-');
        return `${baseUrl}#${handle}`;
      }

      return baseUrl;
    }

    /**
     * Check if Listrak BIS (Back-In-Stock) is enabled
     * @returns {boolean}
     */
    isListrakBISEnabled() {
      return this.productOverview?.dataset.enableListrakBis === 'true';
    }

    /**
     * Check if current variant is unavailable
     * @returns {boolean}
     */
    isVariantUnavailable() {
      const variant = this.productOverview?.getVariant?.();
      if (!variant || Object.keys(variant).length === 0) return true;
      return !variant.available_shopify;
    }

    /**
     * Determines the footer state based on membership, variant availability, and Listrak settings
     * @returns {string} 'membership-required' | 'notify-me' | 'add-to-bag'
     */
    determineFooterState() {
      const membershipValidation = this.validateMembershipAccess();

      // If membership is required and customer doesn't have access
      if (!membershipValidation.hasAccess) {
        return 'membership-required';
      }

      // If variant is unavailable and Listrak BIS is enabled, show notify button
      if (this.isVariantUnavailable() && this.isListrakBISEnabled()) {
        return 'notify-me';
      }

      // Default: show add-to-bag button
      return 'add-to-bag';
    }

    /**
     * Renders the footer content based on membership validation and variant availability
     * Renders one element - add-to-bag button, membership link, or notify button
     */
    updateAddToBagButton() {
      if (!this.footer || !this.productOverview) return;

      const newState = this.determineFooterState();

      // Only re-render if state changed
      if (this.membershipFooterState === newState && this.footer.firstChild) {
        // State hasn't changed, just update the existing element
        if (newState === 'add-to-bag') {
          this.updateAddToBagButtonState();
        } else if (newState === 'notify-me') {
          this.updateNotifyButtonVariantInfo();
        }
        return;
      }

      // Clear existing footer content
      this.footer.innerHTML = '';
      this.membershipFooterState = newState;

      switch (newState) {
        case 'membership-required':
          this.renderMembershipLink();
          break;
        case 'notify-me':
          this.renderNotifyButton();
          break;
        case 'add-to-bag':
        default:
          this.renderAddToBagButton();
          break;
      }
    }

    /**
     * Renders the membership link in the footer
     */
    renderMembershipLink() {
      const productMembership = this.getProductMembership();
      const ctaText = this.getMembershipCTAText();
      const landingPageUrl = this.getMembershipLandingPageURL();

      const groupHandle = productMembership?.membership_group_required?.handle;
      const levelHandle = productMembership?.membership_level_required?.handle;

      // Build class list
      let className = 'button button--black button--full-width product-option-drawer__membership-link';
      if (groupHandle) {
        className += ` button--${groupHandle}`;
      }
      if (levelHandle && levelHandle !== 'member') {
        className += ` button--${levelHandle}`;
      }

      const membershipLink = document.createElement('a');
      membershipLink.className = className;
      membershipLink.href = landingPageUrl;
      membershipLink.textContent = ctaText;

      this.footer.appendChild(membershipLink);
    }

    /**
     * Renders the add-to-bag button in the footer
     */
    renderAddToBagButton() {
      const addToBagButton = document.createElement('button');
      addToBagButton.type = 'button';
      addToBagButton.className = 'button button--blue product-option-drawer__add-to-bag';
      addToBagButton.dataset.addToCart = '';
      addToBagButton.innerHTML = '<span class="button__text">Add to Bag<span class="atc-variant-info desktop-hidden"></span></span>';

      // Add click handler
      addToBagButton.addEventListener('click', () => this.handleAddToBag());

      this.footer.appendChild(addToBagButton);

      // Update button state based on variant availability
      this.updateAddToBagButtonState();
    }

    /**
     * Renders the Listrak notify button in the footer
     */
    renderNotifyButton() {
      const variant = this.productOverview?.getVariant?.();
      const product = this.productOverview?.product;

      // Create the notify button element
      const notifyButton = document.createElement('modal-opener');
      notifyButton.className = 'listrak-notify-button product-option-drawer__notify-button no-js-hidden';
      notifyButton.dataset.modal = '#back-in-stock';

      const variantTitle = variant?.title ? variant.title.replace(' / ', ' - ') : '';
      const showVariantInfo = product && !product.has_only_default_variant && variantTitle;

      notifyButton.innerHTML = `
        <button type="button" class="button button--outline button--full-width js-notify-button">
          <span class="button__text">
            Notify me when available
            ${showVariantInfo ? `<span class="atc-variant-info desktop-hidden">${variantTitle}</span>` : ''}
          </span>
        </button>
      `;

      // Add click listener to close drawer when modal opens
      const button = notifyButton.querySelector('button');
      if (button) {
        button.addEventListener('click', () => {
          // Close the option drawer when opening the notify modal
          this.closeDrawer();
        });
      }

      this.footer.appendChild(notifyButton);
    }

    /**
     * Updates the variant info in the notify button
     */
    updateNotifyButtonVariantInfo() {
      const notifyButton = this.footer.querySelector('.listrak-notify-button');
      if (!notifyButton) return;

      const variant = this.productOverview?.getVariant?.();
      const product = this.productOverview?.product;
      const variantInfoSpan = notifyButton.querySelector('.atc-variant-info');

      if (variantInfoSpan && product?.has_only_default_variant === false) {
        variantInfoSpan.textContent = variant?.title ? variant.title.replace(' / ', ' - ') : '';
      }
    }

    /**
     * Updates the add-to-bag button state (enabled/disabled, text)
     */
    updateAddToBagButtonState() {
      const addToBagButton = this.footer.querySelector('.product-option-drawer__add-to-bag');
      if (!addToBagButton) return;

      const variant = this.productOverview.getVariant();
      const buttonText = addToBagButton.querySelector('.button__text');
      const variantInfo = addToBagButton.querySelector('.atc-variant-info');

      if (!buttonText) return;

      // Update price info
      if (variantInfo && variant && variant.price_html) {
        variantInfo.innerHTML = variant.price_html;
      }

      // Check if variant is available
      if (variant && Object.keys(variant).length > 0 && variant.available_shopify) {
        addToBagButton.removeAttribute('disabled');
        addToBagButton.classList.remove('disabled');
        addToBagButton.dataset.state = 'add-to-bag';
        const textNode = buttonText.childNodes[0];
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          textNode.textContent = 'Add to Bag';
        }
      } else {
        addToBagButton.setAttribute('disabled', 'true');
        addToBagButton.classList.add('disabled');
        addToBagButton.dataset.state = 'sold-out';
        const textNode = buttonText.childNodes[0];
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          textNode.textContent = 'Sold Out';
        }
      }
    }

    disconnectedCallback() {
      // Clean up drawer if it exists
      if (this.drawer && this.drawer.parentNode) {
        this.drawer.parentNode.removeChild(this.drawer);
      }

      // Restore body scroll if drawer was open
      if (this.isDrawerOpen()) {
        document.body.style.overflow = '';
      }
    }
  }

  customElements.define('product-option-drawer', ProductOptionDrawer);
}
